<template>
  <div class="h-100 wrapper">
    <div>
      <b-row class="heading-container">
        <go-back />
        <h4 class="ml-2">
          {{ $t("create_closeout") | startcase }}
        </h4>
      </b-row>
    </div>

    <validation-observer ref="observer" tag="fieldset" v-slot="{ invalid }">
      <!-- Distribution -->
      <b-row class="mt-2">
        <b-col>
          <distribution-form-group
            :label="$t('tariff_period') | capitalize"
            label-cols="2"
            id="distribution"
            vid="distribution"
            ref="distribution"
            v-slot="{ state }"
          >
            <b-form-input
              readonly
              v-model="closeout.distribution"
              :state="state"
            ></b-form-input>
          </distribution-form-group>
        </b-col>
      </b-row>

      <!-- College -->
      <b-row class="mt-2">
        <b-col>
          <distribution-form-group
            :label="$t('college') | capitalize"
            label-cols="2"
            id="college"
            vid="college"
            ref="college"
            v-slot="{ state }"
          >
            <b-form-input
              readonly
              :value="getCollegeText()"
              :state="state"
              disabled
            ></b-form-input>
          </distribution-form-group>
        </b-col>
      </b-row>

      <!-- Total closeout revenue override -->
      <b-row class="mt-2">
        <b-col>
          <distribution-form-group
            :label="$t('remaining_revenue_override') | capitalize"
            label-cols="2"
            id="total_closeout_revenue_override"
            vid="total_closeout_revenue_override"
            ref="total_closeout_revenue_override"
            v-slot="{ state }"
            rules="double|positive"
          >
            <b-form-input
              v-model="closeout.total_closeout_revenue_override"
              :state="state"
              type="number"
              min="0.01"
              step="0.01"
            ></b-form-input>
          </distribution-form-group>
        </b-col>
      </b-row>

      <!-- VPP override -->
      <b-row class="mt-2">
        <b-col>
          <distribution-form-group
            :label="$t('vpp_override') | capitalize"
            label-cols="2"
            id="vpp_override"
            vid="vpp_override"
            ref="vpp_override"
            v-slot="{ state }"
            rules="double|positive|max_decimals_8"
            :description="$t('vpp_override_available_later')"
            disabled
          >
            <b-form-input
              v-model="closeout.vpp_override"
              :state="state"
            ></b-form-input>
          </distribution-form-group>
        </b-col>
      </b-row>
      <!-- Dummy disabled fields for UX purposes -->
      <!-- snapshot taken -->
      <b-row class="mt-2">
        <b-col>
          <distribution-form-group
            :label="$t('snapshot_taken') | capitalize"
            label-cols="2"
            id="snapshot_taken"
            vid="snapshot_taken"
            ref="snapshot_taken"
          >
            <b-form-input disabled />
          </distribution-form-group>
        </b-col>
      </b-row>
      <!-- created by -->
      <b-row class="mt-2">
        <b-col>
          <distribution-form-group
            :label="$t('created_by') | capitalize"
            label-cols="2"
            id="created_by"
            vid="created_by"
            ref="created_by"
          >
            <b-form-input disabled />
          </distribution-form-group>
        </b-col>
      </b-row>
      <!-- Total closeout revenue -->
      <b-row class="mt-2">
        <b-col>
          <distribution-form-group
            :label="$t('remaining_revenue') | capitalize"
            label-cols="2"
            id="total_closeout_revenue"
            vid="total_closeout_revenue"
            ref="total_closeout_revenue"
          >
            <b-form-input disabled />
          </distribution-form-group>
        </b-col>
      </b-row>
      <!-- Original total royalties paid -->
      <b-row class="mt-2">
        <b-col>
          <distribution-form-group
            :label="$t('original_total_royalties_paid') | capitalize"
            label-cols="2"
            id="original_total_royalties_paid"
            vid="original_total_royalties_paid"
            ref="original_total_royalties_paid"
          >
            <b-form-input disabled />
          </distribution-form-group>
        </b-col>
      </b-row>
      <!-- Original total weighted usage paid -->
      <b-row class="mt-2">
        <b-col>
          <distribution-form-group
            :label="$t('original_total_weighted_usage_paid') | capitalize"
            label-cols="2"
            id="original_total_weighted_usage_paid"
            vid="original_total_weighted_usage_paid"
            ref="original_total_weighted_usage_paid"
          >
            <b-form-input disabled />
          </distribution-form-group>
        </b-col>
      </b-row>
      <!-- Total Payments -->
      <b-row class="mt-2">
        <b-col>
          <distribution-form-group
            :label="$t('total_payments') | capitalize"
            label-cols="2"
            id="total_payments"
            vid="total_payments"
            ref="total_payments"
          >
            <b-form-input disabled />
          </distribution-form-group>
        </b-col>
      </b-row>
      <!-- Reserve 1 value and type -->
      <b-row class="mt-2">
        <b-col>
          <distribution-form-group
            vid="reserve1_value"
            ref="reserve1_value"
            :label="
              getReserveAmountLabel({
                reserveNumber: 1,
                type: closeout.reserve_1_value_type,
              })"
            label-cols="2"
            :detect-input="false"
            rules="double|positive"
            v-slot="{ state, validate }"
          >
            <b-input-group>
              <template v-slot:append>
                <b-form-select
                  :options="reserveTypeOptions"
                  v-model="closeout.reserve_1_value_type"
                  @change="validate(closeout.reserve_1_value)"
                />
              </template>
              <b-form-input
                v-model="closeout.reserve_1_value"
                :state="state"
                @input="validate"
              ></b-form-input>
            </b-input-group>
          </distribution-form-group>
        </b-col>
      </b-row>

      <!-- Reserve 1 notes -->
      <b-row class="mt-2">
        <b-col>
          <distribution-form-group
            id="reserve1_notes"
            vid="reserve1_notes"
            ref="reserve1_notes"
            :label="getReserveNotesLabel({ reserveNumber: 1 })"
            label-cols="2"
            v-slot="{ state }"
          >
            <b-form-textarea v-model="closeout.reserve_1_note" :state="state" />
          </distribution-form-group>
        </b-col>
      </b-row>

      <template v-if="!showSecondReserve">
        <b-row class="mt-2">
          <b-col>
            <distribution-form-group
              vid="reserve2_enable"
              ref="reserve2_enable"
              :label="$t('reserve2') | capitalize"
              label-cols="2"
              :detect-input="false"
              v-slot="{ }"
            >
            <b-button class="btn-outline-dark" @click="showSecondReserve = true">
              {{ $t("reserve2_enable") | capitalize }}
            </b-button>
            </distribution-form-group>
          </b-col>
        </b-row>
      </template>

      <template v-if="showSecondReserve">
        <!-- Reserve 2 value and type -->
        <b-row class="mt-2">
          <b-col>
            <distribution-form-group
              vid="reserve2_value"
              ref="reserve2_value"
              :label="getReserveAmountLabel({
                reserveNumber: 2,
                type: closeout.reserve_2_value_type,
              })"
              label-cols="2"
              :detect-input="false"
              rules="double|positive"
              v-slot="{ state, validate }"
            >
              <b-input-group>
                <template v-slot:append>
                  <b-form-select
                    :options="reserveTypeOptions"
                    v-model="closeout.reserve_2_value_type"
                    @change="validate(closeout.reserve_2_value)"
                  />
                </template>
                <b-form-input
                  v-model="closeout.reserve_2_value"
                  :state="state"
                  @input="validate"
                ></b-form-input>
              </b-input-group>
            </distribution-form-group>
          </b-col>
        </b-row>
  
        <!-- Reserve 2 notes -->
        <b-row class="mt-2">
          <b-col>
            <distribution-form-group
              id="reserve2_notes"
              vid="reserve2_notes"
              ref="reserve2_notes"
              :label="getReserveNotesLabel({ reserveNumber: 2 })"
              label-cols="2"
              v-slot="{ state }"
            >
              <b-form-textarea v-model="closeout.reserve_2_note" :state="state" />
            </distribution-form-group>
          </b-col>
        </b-row>
      </template>

      <div class="fixed-footer" ref="footer">
        <div class="d-flex justify-content-end">
          <b-button class="btn-outline-dark" @click="cancel">
            {{ $t("cancel") | capitalize }}</b-button
          >
          <b-button
            id="submit-btn"
            class="ml-3 btn-outline-red"
            @click="submit"
            :disabled="invalid || loading"
            :title="
              (!invalid ? '' : $t('fill_in_the_missing_fields')) | capitalize
            "
          >
            {{ $t("create") | capitalize }}
          </b-button>
        </div>
      </div>
    </validation-observer>

    <modal
      id="cancel-modal"
      cancel-variant="outline-primary"
      ok-variant="outline-secondary"
      :cancel-title="$t('no_continue_editing') | capitalize"
      :ok-title="$t('yes_cancel_editing') | capitalize"
      @ok="realCancel()"
      @cancel="$bvModal.hide('cancel-modal')"
      modal-type="info"
      centered
      :custom-title="$t('create_closeout') | capitalize"
    >
      {{ $t("cancel_create_closeout") }}
    </modal>
    <modal
      id="confirm-modal"
      cancel-variant="outline-primary"
      ok-variant="outline-secondary"
      :cancel-title="$t('cancel') | capitalize"
      :ok-title="$t('confirm') | capitalize"
      @ok="realSubmit()"
      @cancel="$bvModal.hide('confirm-modal')"
      modal-type="info"
      centered
      :custom-title="$t('create_closeout') | capitalize"
    >
      <p>{{ $t("create_closeout_confirm") }}</p>
      <b-row>
        <b-col>
          <span class="font-weight-bold">{{ $t("distribution") | capitalize }}:</span> {{ description }}
        </b-col>
        <b-col>
          <span class="font-weight-bold">{{ $t("college") | capitalize }}:</span> {{ getCollegeText(college) }}
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <span class="font-weight-bold">{{ $t("remaining_revenue_override") | capitalize }}:</span> {{ closeout.total_closeout_revenue_override }}
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <span class="font-weight-bold">{{ $t("vpp_override") | capitalize }}:</span> {{ closeout.vpp_override }}
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <span class="font-weight-bold">
            {{ getReserveAmountLabel({ reserveNumber: 1, type: closeout.reserve_1_value_type }) }}:
          </span> 
          <span v-if="closeout.reserve_1_value">{{ closeout.reserve_1_value }}{{ getReserveValueTypeText(closeout.reserve_1_value_type) }}</span>
        </b-col>
        <b-col>
          <span class="font-weight-bold">
            {{ getReserveNotesLabel({ reserveNumber: 1 }) }}:
          </span> {{ closeout.reserve_1_note }}
        </b-col>
      </b-row>
      <b-row v-if="closeout.reserve_2_value">
        <b-col>
          <span class="font-weight-bold">
            {{ getReserveAmountLabel({ reserveNumber: 2, type: closeout.reserve_2_value_type }) }}:
          </span>
          <span v-if="closeout.reserve_2_value">{{ closeout.reserve_2_value }}{{ getReserveValueTypeText(closeout.reserve_2_value_type) }}</span>
        </b-col>
        <b-col>
          <span class="font-weight-bold">
            {{ getReserveNotesLabel({ reserveNumber: 2 }) }}:
          </span> {{ closeout.reserve_2_note }}
        </b-col>
      </b-row>
    </modal>
  </div>
</template>

<script>
import {
  closeoutReserveValueTypes,
  distributionCollegeTypes,
} from "@/constants"
import DistributionFormGroup from "@/components/DistributionFormGroup"
import { ValidationObserver } from "vee-validate"
import { capitalize } from "lodash"
import { mapActions } from "vuex"

export default {
  name: "CloseoutCreatePage",
  components: { DistributionFormGroup, ValidationObserver },
  props: {
    id: String,
    description: String,
    college: String,
  },
  data () {
    return {
      closeout: {
        distribution: null,
        college: null,
        total_closeout_revenue_override: null,
        vpp_override: null,
        reserve_1_value_type: "percent",
        reserve_1_value: null,
        reserve_1_note: null,
        reserve_2_value_type: "percent",
        reserve_2_value: null,
        reserve_2_note: null,
      },
      reserveTypeOptions: [
        {
          text: "%",
          value: closeoutReserveValueTypes.PERCENT,
        },
        {
          text: "CAD",
          value: closeoutReserveValueTypes.MONEY,
        },
      ],
      showSecondReserve: false,
      loading: false,
    }
  },
  methods: {
    ...mapActions("alert", ["success", "error"]),
    async validate () {
      return await this.$refs.observer.validate()
    },
    getCollegeText () {
      if (this.closeout.college) {
        if (this.closeout.college === distributionCollegeTypes.MAKER) {
          return capitalize(this.$t(distributionCollegeTypes.MAKER))
        } 
        else if (this.closeout.college === distributionCollegeTypes.PERFORMER) {
          return capitalize(this.$t(distributionCollegeTypes.PERFORMER))
        }
      }
    },
    getReserveAmountLabel ({ reserveNumber, type }) {
      const reserveNumberLabel = this.showSecondReserve ? reserveNumber : ""
      const typeLabel = type === "percent" ? "%" : this.$t("amount")

      return capitalize(
        `${this.$t("reserve")} ${reserveNumberLabel} ${typeLabel}`,
      )
    },
    getReserveNotesLabel ({ reserveNumber }) {
      const reserveNumberLabel = this.showSecondReserve ? reserveNumber : ""

      return capitalize(
        `${this.$t("reserve")} ${reserveNumberLabel} ${this.$t("notes")}`,
      )
    },
    getReserveValueTypeText (reserveValueType) {
      const reserveType = this.reserveTypeOptions.find(
        (option) => option.value === reserveValueType
      )
      return reserveType ? reserveType.text : ""
    },
    cancel () {
      this.$bvModal.show("cancel-modal")
    },
    realCancel () {
      this.success(this.$t("closeout_creation_cancelled"))
      this.$router.go(-1)
    },
    submit () {
      this.$bvModal.show("confirm-modal")
    },
    realSubmit () {
      this.loading = true

      this.$api.distributions.createCloseout({
        distributionId: this.id,
        ...Object.fromEntries(Object.entries(this.closeout).map(([k, v]) => [k, v === "" ? null : v])),
      }).then(() => {
        this.$router.push({
          path: "/distributions/"
        }).then(() => {
          this.success(capitalize(this.$t("closeout_new_created")))
        })
      }).catch(e => {
        let message = ""
        if (e.response.status === 422) {
          message = "Failed to create closeout. Please correct the data and try again."
          if (e.response.data.fieldErrors) {
            message = message + "\n" + e.response.data.fieldErrors.map(e => e.message).join("\n")
          }
          if (e.response.data.nonFieldErrors) {
            message = message + "\n" + e.response.data.nonFieldErrors.map(e => e.message).join("\n")
          }
        } else {
          message = "Failed to create closeout. Please contact administrator or try again."
        }
        this.error(message)
      }).finally(() => {
        this.loading = false
      })
    },
  },
  mounted () {
    this.closeout.distribution = this.description
    if (this.college) {
      this.closeout.college = this.college
    }
  },
}
</script>

<style lang="scss" scoped>
.wrapper {
  padding-bottom: 30px;
}

.fixed-footer {
  position: fixed;
  height: 30px;
  bottom: 40px;
  left: 0;
  width: calc(100% - 15px);
}

::v-deep .input-group-append .custom-select {
  background-color: #fff;
}
</style>
